"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useId } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Header 5015 — KUK landing-page site header.
 *
 * Port of the landing-page branch of kuk-campus-portal
 * `components/organisms/Header.tsx`: a white bar with the university logo on the
 * left and, on the right, a green WhatsApp "Reach Out" link, a phone line and an
 * Apply CTA. The reference gates the WhatsApp link and the phone line on
 * `pathname.includes("lp")`, so this is the arrangement its landing pages get —
 * the site nav, mega-menu and account controls do not render there.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:`
 * is 768px. This app remaps `md:` to 1230px and exposes 768px as `tab:` (see
 * globals.css `--breakpoint-*`), so every `md:` in the reference is ported as
 * `tab:`.
 *
 * WHY NOT HEADER 5001 — that variant fills its WhatsApp control as a solid
 * button, which is the BDU design. KUK's is a borderless green text link with
 * the glyph inline, and its phone line is plain text rather than a pill. Same
 * columns, different bar; both are worth having.
 *
 * ROUTE SNIFFING DROPPED. v1 decides what to show by reading the URL —
 * `pathname.includes("lp")` for the WhatsApp and phone controls,
 * `isUserVerified && hasErpLeadId` for the launchpad link. A widget should not
 * inspect the route to decide its own shape, so every control here is an
 * explicit CMS toggle. A page that wants the reference's landing-page bar turns
 * on exactly the three controls it shows.
 *
 * DESKTOP-ONLY CONTROLS — v1 hides both the WhatsApp link and the phone line
 * below `tab` (`hidden md:flex`), leaving the mobile bar as logo + Apply. Kept:
 * the two together are wider than a phone bar can hold beside a logo.
 *
 * DROPPED — v1's launchpad "Refer & Earn" link, which only renders for a
 * signed-in user with an ERP lead id. That is account chrome, not landing-page
 * content, and this app resolves signed-in state elsewhere.
 */

/** v1: `width="20" height="20"` on both the WhatsApp and phone glyphs. */
const ICON_SIZE = "20";

/**
 * Reference bar heights: 50px below 768, 64px from 768, 65px from its `lg`
 * (1024px).
 *
 * THE DESKTOP SWITCH IS 1024, NOT THIS APP'S `sm` (1080). Using `sm:` left a
 * 56px band (1024–1080) rendering the tablet bar where the reference already
 * shows the desktop one — wrong height, wrong padding, no wordmark, no shadow.
 * `min-[1024px]:` hits v1's breakpoint exactly.
 *
 * These are written out as whole class names on purpose: Tailwind scans raw
 * source text, so a composed `${PREFIX}h-[65px]` would never be generated.
 */
const BAR_HEIGHT = "h-[50px] tab:h-[64px] min-[1024px]:h-[65px]";
/** v1: `max-w-[2024px]` on the desktop bar. */
const BAR_MAX_WIDTH = "max-w-[2024px]";
/**
 * Logo geometry, read off the reference's own `<img>` attributes rather than
 * eyeballed — see kuk-reference/online-mba-lp.html. The source files are
 * 31x36 (crest) and 540x156 (wordmark, 3.46:1).
 *
 *   bar      element    attrs    class                  renders
 *   desktop  crest      31x35    w-[31px] h-auto        31 wide, natural height
 *   desktop  wordmark   135x32   (none)                 FORCED 135x32 (4.22:1)
 *   tablet   crest      42x28    w-auto h-auto          42x28 from the attrs
 *   tablet   wordmark   —        —                      absent
 *   mobile   crest      40x40    w-[31px] h-[35px]      FORCED 31x35
 *   mobile   wordmark   186x32   w-auto                 ~111 wide, natural
 *
 * THE MOBILE `w-auto` IS THE POINT: it overrides the 186 width attribute, so the
 * wordmark keeps its 3.46:1 ratio at ~111px. Reading 186 as a literal width and
 * pinning `w-[186px]` next to `h-8` forces a 5.81:1 box and stretches the
 * lettering ~68% — which is what the wordmark looked wrong at.
 *
 * The desktop 135x32 IS a real (mild, 22%) stretch that the reference performs;
 * it is reproduced rather than "corrected", so the two sites match.
 */
/* Intrinsic attrs. The crest file is 31x36; the wordmark 540x156. */
const CREST_W = 31;
const CREST_H = 35;
const WORDMARK_H = 32;

const CREST_CLASS =
  "h-[35px] w-[31px] tab:h-[28px] tab:w-[42px] min-[1024px]:h-auto min-[1024px]:w-[31px]";
/**
 * Height-driven at every width, so the wordmark keeps its true 3.46:1 ratio
 * (111x32). Hidden in the tablet band, as in the reference.
 *
 * DELIBERATE DIVERGENCE. The reference pins `width="135" height="32"` with no
 * CSS on a 540x156 file, which stretches the lettering 22% horizontally on
 * desktop. Reproducing that faithfully was worse than fixing it, so the width
 * is left to follow the height. The result is 24px narrower than the live
 * desktop logo and identical to the reference's own mobile bar.
 */
/* The two reference bars size the wordmark differently, and the difference is
   real rather than incidental:
     mobile  <img width="186" height="32" class="w-auto">  -> w-auto cancels the
             attribute, so it renders at its natural ratio: 32 * (540/156) = 111px.
     desktop <img width="135" height="32">  (no class)      -> nothing cancels the
             attributes, so it renders 135x32 -- 22% wider than natural.
   Reproducing both means pinning 135px at desktop and leaving w-auto below it. */
/* The reference is Tailwind v3.4 with no borderRadius override, so its
   `rounded-sm` is the stock v3 value: 0.125rem = 2px. This app is Tailwind
   v4, where `rounded-sm` was rebased to 0.25rem = 4px (v3's value moved to
   `rounded-xs`, which this project does not define -- no --radius-xs). So
   the Apply Now button uses rounded-[2px] to keep the reference's radius.
   `rounded-md` is 0.375rem in both versions and needs no such treatment. */
const WORDMARK_CLASS =
  "block h-8 w-auto tab:hidden min-[1024px]:block min-[1024px]:w-[135px]";


/**
 * The reference embeds these two marks as inline SVG, and both are TWO-TONE: the
 * WhatsApp glyph is a green gradient disc with a white handset, and the phone is
 * a solid red disc with a white handset and a light ring. `DynamicIcon` tints a
 * single colour, so neither can be reproduced through it — the iconMap keys
 * `whatsapp` / `phone` in this app resolve to different, single-colour art.
 * Carried over verbatim instead, exactly as the reference ships them.
 *
 * The gradient/clip/filter ids are scoped per instance: the bar renders once,
 * but two of these on one page would otherwise collide on a global id and the
 * second would inherit the first's paint.
 */
const WhatsAppMark = ({ size, uid }: { size: string; uid: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 31 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g clipPath={`url(#wa-clip-${uid})`}>
      <path
        d="M1.61912 15.3265C1.61844 17.7631 2.26006 20.1422 3.4801 22.2392L1.50244 29.404L8.89199 27.4815C10.9359 28.5855 13.2258 29.164 15.5529 29.1641H15.559C23.2412 29.1641 29.4946 22.9614 29.4979 15.3374C29.4994 11.6431 28.0508 8.16914 25.4188 5.55554C22.7873 2.94217 19.2875 1.50217 15.5585 1.50049C7.87541 1.50049 1.62241 7.70289 1.61923 15.3265"
        fill={`url(#wa-a-${uid})`}
      />
      <path
        d="M1.12121 15.322C1.12042 17.8462 1.78504 20.3104 3.04857 22.4825L1 29.9041L8.65451 27.9127C10.7636 29.0537 13.1382 29.6553 15.5545 29.6562H15.5607C23.5185 29.6562 29.9965 23.2303 29.9999 15.3334C30.0013 11.5063 28.5006 7.90751 25.7746 5.20028C23.0482 2.49339 19.4232 1.00157 15.5607 1C7.60156 1 1.12438 7.42496 1.12121 15.322ZM5.67975 22.1084L5.39394 21.6582C4.19249 19.7627 3.55834 17.5722 3.55925 15.3229C3.56174 8.75907 8.94531 3.41891 15.5652 3.41891C18.7711 3.42026 21.7839 4.66029 24.05 6.91016C26.3159 9.16024 27.5628 12.1513 27.562 15.3325C27.5591 21.8963 22.1754 27.2371 15.5607 27.2371H15.5559C13.4021 27.236 11.2898 26.6621 9.44759 25.5775L9.0092 25.3195L4.46685 26.5012L5.67975 22.1084Z"
        fill={`url(#wa-b-${uid})`}
      />
      <path
        d="M11.9517 9.3344C11.6814 8.73833 11.397 8.7263 11.1399 8.71585C10.9295 8.70686 10.6889 8.70753 10.4485 8.70753C10.2079 8.70753 9.81693 8.79734 9.48649 9.15535C9.15571 9.51369 8.22363 10.3796 8.22363 12.1409C8.22363 13.9021 9.51651 15.6044 9.69674 15.8434C9.8772 16.0821 12.1927 19.8121 15.8598 21.247C18.9075 22.4395 19.5277 22.2023 20.1892 22.1425C20.8507 22.0829 22.3238 21.2768 22.6244 20.4408C22.9251 19.605 22.9251 18.8886 22.835 18.7388C22.7448 18.5897 22.5042 18.5001 22.1434 18.3211C21.7826 18.1422 20.0087 17.276 19.678 17.1565C19.3473 17.0372 19.1068 16.9776 18.8662 17.336C18.6256 17.6939 17.9346 18.5001 17.7241 18.7388C17.5137 18.9781 17.3031 19.0079 16.9424 18.8289C16.5814 18.6492 15.4195 18.2717 14.041 17.0522C12.9684 16.1033 12.2443 14.9315 12.0338 14.5731C11.8234 14.2152 12.0113 14.0212 12.1922 13.8428C12.3543 13.6824 12.5531 13.4248 12.7337 13.2158C12.9136 13.0067 12.9736 12.8576 13.0939 12.6188C13.2143 12.3799 13.1541 12.1708 13.064 11.9917C12.9736 11.8127 12.2725 10.0422 11.9517 9.3344Z"
        fill="white"
      />
    </g>
    <defs>
      <linearGradient id={`wa-a-${uid}`} x1="1401.28" y1="2791.85" x2="1401.28" y2="1.50049" gradientUnits="userSpaceOnUse">
        <stop stopColor="#1FAF38" />
        <stop offset="1" stopColor="#60D669" />
      </linearGradient>
      <linearGradient id={`wa-b-${uid}`} x1="1451" y1="2891.41" x2="1451" y2="1" gradientUnits="userSpaceOnUse">
        <stop stopColor="#F9F9F9" />
        <stop offset="1" stopColor="white" />
      </linearGradient>
      <clipPath id={`wa-clip-${uid}`}>
        <rect width="29" height="29" fill="white" transform="translate(1 1)" />
      </clipPath>
    </defs>
  </svg>
);

/** v1's phone mark: a solid disc with a light ring and a white handset. */
const PhoneMark = ({ size, color }: { size: string; color: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 81 81"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <rect x="8" y="4" width="65.75" height="65.75" rx="32.875" fill={color} />
    <rect x="8.5" y="4.5" width="64.75" height="64.75" rx="32.375" stroke="#E8E8E8" />
    <path
      d="M55.7813 53.75C51.875 53.75 48.0156 52.8988 44.2031 51.1963C40.3906 49.4938 36.9219 47.0794 33.7969 43.9531C30.6719 40.8269 28.2581 37.3581 26.5556 33.5469C24.8531 29.7356 24.0012 25.8763 24 21.9688C24 21.4063 24.1875 20.9375 24.5625 20.5625C24.9375 20.1875 25.4063 20 25.9688 20H33.5625C34 20 34.3906 20.1487 34.7344 20.4462C35.0781 20.7437 35.2813 21.095 35.3438 21.5L36.5625 28.0625C36.625 28.5625 36.6094 28.9844 36.5156 29.3281C36.4219 29.6719 36.25 29.9687 36 30.2187L31.4531 34.8125C32.0781 35.9687 32.82 37.0856 33.6787 38.1631C34.5375 39.2406 35.4831 40.28 36.5156 41.2813C37.4844 42.25 38.5 43.1488 39.5625 43.9775C40.625 44.8063 41.75 45.5638 42.9375 46.25L47.3437 41.8437C47.625 41.5625 47.9925 41.3519 48.4463 41.2119C48.9 41.0719 49.345 41.0325 49.7812 41.0938L56.25 42.4062C56.6875 42.5312 57.0469 42.7581 57.3281 43.0869C57.6094 43.4156 57.75 43.7825 57.75 44.1875V51.7813C57.75 52.3438 57.5625 52.8125 57.1875 53.1875C56.8125 53.5625 56.3438 53.75 55.7813 53.75Z"
      fill="white"
    />
  </svg>
);

/** Only an uploaded URL overrides the marks above; iconMap keys cannot. */
const isUploadedIcon = (v: string) =>
  v.startsWith("http://") || v.startsWith("https://");

interface Header5015Data {
  status?: boolean | number | string;
  background_color?: string;
  show_bar_shadow?: boolean | number | string;
  sticky_to_top?: boolean | number | string;
  show_on_mobile?: boolean | number | string;
  /**
   * Logo. The reference is a PAIR: a small crest beside a wider wordmark. The
   * wordmark is hidden in the 768-1080 band, exactly as v1 hides it on tablet.
   */
  show_logo?: boolean | number | string;
  logo_image?: string;
  logo?: string;
  logo_alt_text?: string;
  link_href?: string;
  /** WhatsApp "Reach Out" — a borderless green text link, desktop only. */
  whatsapp_show?: boolean | number | string;
  whatsapp_button_text?: string;
  whatsapp_link?: string;
  whatsapp_number?: string;
  whatsapp_icon?: string;
  whatsapp_button_text_color?: string;
  /** Phone line — plain text, desktop only. */
  phone_number?: string;
  phone_text_color?: string;
  /** Fill of the phone disc. Distinct from the number's text colour. */
  pill_background_color?: string;
  phone_icon?: string;
  phone_icon_alt?: string;
  /** Apply CTA */
  show_apply_button?: boolean | number | string;
  apply_button_text?: string;
  button_link?: string;
  button_color?: string;
  button_text_color?: string;
}

/**
 * A nullable CMS column, not a string: these come straight from MySQL, where an
 * unset URL is NULL — and a destructuring default only fills in `undefined`, so
 * `null` reaches here intact.
 */
function normalizeHref(href: string | null | undefined): string {
  const h = (href ?? "").trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

/** A bare number becomes a wa.me link; anything already a URL is left alone. */
function whatsappHref(
  link: string | null | undefined,
  number: string | null | undefined,
): string {
  if ((link ?? "").trim()) return normalizeHref(link);
  const digits = (number ?? "").replace(/[^\d]/g, "");
  return digits ? `https://wa.me/${digits}` : "#";
}

const Header5015 = (props: { data?: Header5015Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Header5015Data | undefined;
  const dispatch = useDispatch();
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");

  const {
    background_color = "#FFFFFF",
    logo_image = "",
    logo: wordmark_image = "",
    logo_alt_text = "",
    link_href = "",
    whatsapp_button_text = "Reach Out",
    whatsapp_link = "",
    whatsapp_number = "",
    whatsapp_icon = "",
    whatsapp_button_text_color = "#61D76A",
    phone_number = "",
    phone_text_color = "#101112",
    pill_background_color: phone_badge_color = "#9F1D33",
    phone_icon = "",
    phone_icon_alt = "",
    apply_button_text = "Apply Now",
    button_link = "",
    button_color = "#9F1C33",
    button_text_color = "#FFFFFF",
  } = sectionData || {};

  const status = isCmsShowFlagOn(sectionData?.status, true);
  const showLogo = isCmsShowFlagOn(sectionData?.show_logo, true);
  const showWhatsapp = isCmsShowFlagOn(sectionData?.whatsapp_show, true);
  const showApply = isCmsShowFlagOn(sectionData?.show_apply_button, true);
  const showOnMobile = isCmsShowFlagOn(sectionData?.show_on_mobile, true);
  const stickyToTop = isCmsShowFlagOn(sectionData?.sticky_to_top, true);
  const showShadow = isCmsShowFlagOn(sectionData?.show_bar_shadow, true);

  const handleApplyClick = useCallback(() => {
    ctaClick({
      section: "header_5015",
      component: "header_apply_now",
      label: apply_button_text?.trim() || "apply_now",
      step: "1",
      entity: "header_5015",
      parentEntity: "header_5015",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_button_text, dispatch]);

  if (!status) return null;

  const logo =
    showLogo && (logo_image || wordmark_image) ? (
      <span className="flex items-center gap-2">
        {logo_image ? (
          <Image
            src={logo_image}
            alt={logo_alt_text || ""}
            width={CREST_W}
            height={CREST_H}
            priority
            loading="eager"
            quality={90}
            className={CREST_CLASS}
          />
        ) : null}
        {wordmark_image ? (
          /* v1 drops the wordmark on tablet and brings it back on desktop. */
          <Image
            src={wordmark_image}
            alt={logo_image ? "" : logo_alt_text || ""}
            width={186}
            height={WORDMARK_H}
            priority
            loading="eager"
            quality={90}
            className={WORDMARK_CLASS}
          />
        ) : null}
      </span>
    ) : null;

  return (
    <header
      className={cn(
        "z-50 w-full",
        !showOnMobile && "hidden tab:block",
        stickyToTop && "sticky top-0",
        /* v1 carries `shadow-md` on the DESKTOP shell only; its tablet and
           mobile bars have none. */
        showShadow && "min-[1024px]:shadow-md",
      )}
      style={{ backgroundColor: background_color }}
    >
      <div
        className={cn(
          "mx-auto flex w-full items-center justify-between gap-3",
          BAR_MAX_WIDTH,
          BAR_HEIGHT,
          /* v1: px-3 on the mobile bar, px-5 on tablet, pl-6/pr-6 on desktop. */
          /* v1's padding is per-bar AND asymmetric: the logo side is px-3
             (mobile), px-5 (tablet), pl-6 (desktop); the control side is
             px-3 on mobile AND tablet, pr-6 on desktop. A single `tab:px-5`
             made the tablet right edge 20px where the reference has 12px. */
          "pl-3 pr-3 tab:pl-5 min-[1024px]:pl-6 min-[1024px]:pr-6",
        )}
      >
        <div className="flex min-w-0 items-center">
          {logo ? (
            link_href ? (
              <Link href={normalizeHref(link_href)} aria-label={logo_alt_text || "Home"}>
                {logo}
              </Link>
            ) : (
              logo
            )
          ) : null}
        </div>

        {/* v1: `space-x-3 px-3` on the mobile/tablet bars, `gap-2 pr-6` on the
            desktop one. `gap-4` here was double the desktop spacing. */}
        <div className="flex items-center justify-end gap-3 min-[1024px]:gap-2">
          {/* v1: `hidden md:flex` — desktop only. */}
          {showWhatsapp && whatsapp_button_text ? (
            <a
              href={whatsappHref(whatsapp_link, whatsapp_number)}
              target="_blank"
              rel="noopener noreferrer"
              /* No gap class: the reference spaces glyph from label with an
                 nbsp text node instead (see below). */
              className="hidden h-7 items-center px-5 py-2 text-xs font-medium tab:flex"
              style={{ color: whatsapp_button_text_color }}
            >
              {isUploadedIcon(whatsapp_icon) ? (
                <DynamicIcon
                  iconName={whatsapp_icon}
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  color={whatsapp_button_text_color}
                />
              ) : (
                <WhatsAppMark size={ICON_SIZE} uid={uid} />
              )}
              {/* v1 spaces the glyph from the label with a text node, not CSS:
                 `</svg>&nbsp; Reach Out`. Kept inside the span so the nbsp can
                 never become a whitespace-only anonymous flex item. */}
              <span className="whitespace-nowrap">{"\u00A0 "}{whatsapp_button_text}</span>
            </a>
          ) : null}

          {/* v1: `hidden md:flex`, bold label with a normal-weight number. */}
          {phone_number ? (
            <p
              /* No gap class: spacing is the <span>&nbsp;</span> below. */
              className="hidden items-center text-sm font-bold tab:flex"
              style={{ color: phone_text_color }}
            >
              {isUploadedIcon(phone_icon) ? (
                <DynamicIcon
                  iconName={phone_icon}
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  color={phone_text_color}
                />
              ) : (
                <PhoneMark size={ICON_SIZE} color={phone_badge_color} />
              )}
              {/* v1: a dedicated <span>&nbsp;</span> between glyph and number. */}
              <span aria-hidden="true">{"\u00A0"}</span>
              <a href={`tel:${(phone_number ?? "").replace(/\s+/g, "")}`} className="whitespace-nowrap font-normal">
                {phone_number}
              </a>
              {phone_icon_alt ? <span className="sr-only">{phone_icon_alt}</span> : null}
            </p>
          ) : null}

          {showApply && apply_button_text ? (
            button_link ? (
              <Link
                href={normalizeHref(button_link)}
                className="inline-flex h-7 items-center justify-center whitespace-nowrap rounded-md px-5 py-2 text-xs font-medium shadow transition-colors tab:rounded-[2px]"
                style={{ backgroundColor: button_color, color: button_text_color }}
              >
                {apply_button_text}
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleApplyClick}
                className="inline-flex h-7 items-center justify-center whitespace-nowrap rounded-md px-5 py-2 text-xs font-medium shadow transition-colors tab:rounded-[2px]"
                style={{ backgroundColor: button_color, color: button_text_color }}
              >
                {apply_button_text}
              </button>
            )
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default Header5015;
